import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { DayPicker } from 'components/DayPicker';
import MIcon from 'views/MIcon/MIcon';

import classes from './MDatePicker.module.scss';
import DatePickerFooter from './DatePickerFooter/DatePickerFooter';
import classNames from 'classnames';
import { InputType } from './MDatePicker.model';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

type Props = {
  className?: string;
  inputClassName?: string;
  datePicker?: moment.Moment | null;
  showSelectedMonth?: boolean;
  time?: string;
  inputType?: InputType;
  dateFormat: string;
  position: 'top' | 'bottom';
  datePickerRef: React.RefObject<HTMLDivElement>;
  isOpenPicker: boolean;
  hideActions?: boolean;
  withTime?: boolean;
  maxDate?: Moment;
  minDate?: Moment;
  fromYear?: number;
  toYear?: number;
  dropdown?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  error?: string | null;
  showAsGmt?: boolean;
  timezone?: string;
  resetTo?: Moment;
  onDatePickerChange: (date?: Date, time?: string, isTimeOnly?: boolean) => void;
  onOpen: () => void;
  onClose: () => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  reset: (event: React.MouseEvent<HTMLDivElement>) => void;
  nextDay: () => void;
  prevDay: () => void;
};

const MDatePickerView: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  return (
    <div className={classNames(classes['container'], props.className)} onBlur={props.onBlur} tabIndex={-1}>
      <div className={classes['content']}>
        {props.hideActions ? null : (
          <div className={classes['btnSteps']} onClick={props.prevDay}>
            <MIcon name={IoChevronBack}></MIcon>
          </div>
        )}
        <div className={classes['inputContent']} onClick={props.onOpen}>
          <input
            disabled={props.disabled}
            className={classNames(classes['input'], { [classes['input--error']]: props.invalid }, props.inputClassName)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (!props.datePicker) {
                return;
              }

              const parsedInput = props.timezone
                ? moment.tz(event.target.value, props.dateFormat, props.timezone)
                : moment(event.target.value, props.dateFormat);

              if (event.target.value.length === 16 && parsedInput.isValid()) {
                props.onDatePickerChange(
                  parsedInput.toDate(),
                  parsedInput.format('HH:mm'),
                );
              }
            }}
            type={props.datePicker ? props.inputType : 'text'}
            value={props.datePicker?.format(props.dateFormat) || ''}
          ></input>
          {props.showAsGmt && props.datePicker ? <span className={classes['gmt']}>{`GMT${props.datePicker.format('Z')}`}</span> : null}
          {props.datePicker && props.clearable ? (
            <div
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                !props.disabled && props.onDatePickerChange();
              }}
              className={classes['btnReset']}
            >
              <MIcon name="close"></MIcon>
            </div>
          ) : null}
          {props.datePicker?.format(props.dateFormat) !== (props.resetTo ?? moment()).format(props.dateFormat) ? (
            <div
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                !props.disabled && props.reset(event);
              }}
              className={classNames(classes['btnReset'], { [classes['btnReset--isPadded']]: props.datePicker && props.clearable })}
            >
              <MIcon name="resetDate"></MIcon>
            </div>
          ) : null}
        </div>
        {props.hideActions ? null : (
          <div className={classes['btnSteps']} onClick={props.nextDay}>
            <MIcon name={IoChevronForward}></MIcon>
          </div>
        )}
      </div>

      {props.isOpenPicker ? (
        <div
          className={classNames(classes['datePicker'], {
            [classes['datePicker--positionBottom']]: props.position === 'top',
            [classes['datePicker--positionTop']]: props.position === 'bottom',
          })}
          ref={props.datePickerRef}
          tabIndex={-1}
        >
          <DayPicker
            weekStartsOn={1}
            mode="single"
            selected={props.datePicker?.toDate()}
            modifiersClassNames={{ selected: classes['mySelected'], today: classes['myToday'] }}
            disabled={{ from: props.minDate?.toDate(), to: props.maxDate?.toDate() }}
            onSelect={(date: Date | undefined) => {
              if (!date) {
                return;
              }

              props.onDatePickerChange(date, props.time);
            }}
            defaultMonth={props.showSelectedMonth ? props.datePicker?.toDate() : undefined}
            captionLayout={(props.dropdown && 'dropdown') || undefined}
            fromYear={props.fromYear}
            toYear={props.toYear}
            footer={
              props.withTime ? (
                <DatePickerFooter
                  time={props.time}
                  onUpdate={(time: string) => props.onDatePickerChange(props.datePicker?.toDate(), time, true)}
                />
              ) : null
            }
          />
        </div>
      ) : null}
      {props.invalid && props.error ? <span className={classes['errorText']}>{props.error}</span> : null}
    </div>
  );
};

MDatePickerView.displayName = 'MDatePickerView';
MDatePickerView.defaultProps = {
  inputType: InputType.Date,
};

export default MDatePickerView;
